import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import { trackEvent } from '../lib/analytics';

export const ContactForm = ({ source = 'general', onSuccess }) => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    country: '',
    interest: '',
    message: '',
    website: '', // honeypot - must remain empty
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          source,
          locale: language,
        }),
      });

      if (response.status === 429) {
        toast({
          title: language === 'es' ? 'Demasiadas solicitudes' : 'Too many requests',
          description: language === 'es'
            ? 'Has alcanzado el límite. Intenta de nuevo más tarde.'
            : "You've reached the limit. Please try again later.",
          variant: 'destructive',
        });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      trackEvent('generate_lead', { source, interest: formData.interest });

      toast({
        title: t('form.success'),
        description: language === 'es'
          ? 'Nos pondremos en contacto pronto.'
          : "We'll be in touch soon.",
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        company: '',
        country: '',
        interest: '',
        message: '',
        website: '',
      });

      if (onSuccess) onSuccess();
    } catch (error) {
      toast({
        title: t('form.error'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
      {/* Honeypot field - hidden from real users, bots will fill it */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        <label htmlFor="website-field">Website (leave blank)</label>
        <input
          id="website-field"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={formData.website}
          onChange={(e) => handleChange('website', e.target.value)}
          data-testid="form-honeypot"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="name">{t('form.name')} *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            data-testid="form-name"
          />
        </div>
        <div>
          <Label htmlFor="email">{t('form.email')} *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            required
            data-testid="form-email"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="company">{t('form.company')} *</Label>
          <Input
            id="company"
            value={formData.company}
            onChange={(e) => handleChange('company', e.target.value)}
            required
            data-testid="form-company"
          />
        </div>
        <div>
          <Label htmlFor="country">{t('form.country')} *</Label>
          <Input
            id="country"
            value={formData.country}
            onChange={(e) => handleChange('country', e.target.value)}
            required
            data-testid="form-country"
          />
        </div>
      </div>

      <div>
        <Label htmlFor="interest">{t('form.interest')} *</Label>
        <Select value={formData.interest} onValueChange={(value) => handleChange('interest', value)} required>
          <SelectTrigger data-testid="form-interest">
            <SelectValue placeholder={language === 'es' ? 'Selecciona una opción' : 'Select an option'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="offtake">{t('form.interests.offtake')}</SelectItem>
            <SelectItem value="investment">{t('form.interests.investment')}</SelectItem>
            <SelectItem value="epc">{t('form.interests.epc')}</SelectItem>
            <SelectItem value="logistics">{t('form.interests.logistics')}</SelectItem>
            <SelectItem value="institutional">{t('form.interests.institutional')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="message">{t('form.message')}</Label>
        <Textarea
          id="message"
          value={formData.message}
          onChange={(e) => handleChange('message', e.target.value)}
          rows={4}
          data-testid="form-message"
        />
      </div>

      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-cta-green-600 hover:bg-cta-green-500 text-white text-lg"
        data-testid="form-submit"
      >
        {loading ? t('form.submitting') : t('form.submit')}
      </Button>
    </form>
  );
};
